/**
 * Opening and downloading mail attachments on the web: the chip shows a spinner and how much has come, then a check for
 * a moment; a failure stays in a toast with "Try again" until it's dismissed. The desktop app also says where it saved
 * the file, with "Show in folder".
 */
import type { MailAttachment } from "@bundle/contracts";
import { ApiFailure } from "@bundle/core";
import { Toast, useT, type MailServices } from "@bundle/ui";
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";

export type AttachmentPhase = "busy" | "done";
export type AttachmentIntent = "open" | "download";
export type DownloadableAttachment = Pick<MailAttachment, "id" | "filename" | "contentType"> & { size?: number };
export type AttachmentState = { phase: AttachmentPhase; intent: AttachmentIntent; /** 0…1, once the size is known. */ progress?: number };

type Notice =
  | { kind: "failed"; seq: number; messageId: string; attachment: DownloadableAttachment; intent: AttachmentIntent; offline: boolean }
  | { kind: "saved"; seq: number; name: string; path: string };

/** How long the check stays on the chip. */
const DONE_MS = 2000;

export function useAttachmentDownloads(mail: Pick<MailServices, "openAttachment" | "revealAttachment">): {
  state: (attachmentId: string) => AttachmentState | undefined;
  /** Call from the click: opening in a browser tab needs it. */
  start: (messageId: string, attachment: DownloadableAttachment, intent?: AttachmentIntent) => void;
  toast: ReactElement;
} {
  const t = useT();
  const { openAttachment, revealAttachment } = mail;
  const [states, setStates] = useState<Record<string, AttachmentState>>({});
  const [notice, setNotice] = useState<Notice>();
  const running = useRef(new Set<string>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const seq = useRef(0);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const set = useCallback((id: string, state: AttachmentState | undefined) => {
    setStates((current) => {
      const { [id]: _removed, ...rest } = current;
      return state === undefined ? rest : { ...rest, [id]: state };
    });
  }, []);

  const start = useCallback(
    (messageId: string, attachment: DownloadableAttachment, intent: AttachmentIntent = "open") => {
      if (running.current.has(attachment.id)) return;
      running.current.add(attachment.id);
      clearTimeout(timers.current.get(attachment.id));
      set(attachment.id, { phase: "busy", intent });
      setNotice(undefined);
      openAttachment(messageId, attachment, intent, (progress) => set(attachment.id, { phase: "busy", intent, progress }))
        .then((outcome) => {
          set(attachment.id, { phase: "done", intent });
          timers.current.set(attachment.id, setTimeout(() => set(attachment.id, undefined), DONE_MS));
          // The desktop app says where it went (Chrome's download bubble), opened or not.
          if (outcome.savedPath !== undefined) setNotice({ kind: "saved", seq: ++seq.current, name: attachment.filename, path: outcome.savedPath });
        })
        .catch((e: unknown) => {
          set(attachment.id, undefined);
          setNotice({ kind: "failed", seq: ++seq.current, messageId, attachment, intent, offline: e instanceof ApiFailure && e.kind === "network" });
        })
        .finally(() => running.current.delete(attachment.id));
    },
    [openAttachment, set],
  );

  const state = useCallback((attachmentId: string) => states[attachmentId], [states]);
  const toast =
    notice?.kind === "saved" ? (
      <Toast
        key={notice.seq}
        open
        onClose={() => setNotice(undefined)}
        message={t("mail.attachment.savedToDownloads", { name: notice.name })}
        {...(revealAttachment === undefined ? {} : { action: { label: t("mail.attachment.showInFolder"), onPress: () => revealAttachment(notice.path), testID: "mail-attachment-reveal" } })}
        closeLabel={t("common.close")}
        testID="mail-attachment-saved"
      />
    ) : (
      <Toast
        key={notice?.seq}
        open={notice !== undefined}
        onClose={() => setNotice(undefined)}
        message={
          notice?.offline === true ? t("mail.attachment.offlineNamed", { name: notice.attachment.filename }) : t("mail.attachment.failedNamed", { name: notice?.attachment.filename ?? "" })
        }
        {...(notice === undefined ? {} : { action: { label: t("common.retry"), onPress: () => start(notice.messageId, notice.attachment, notice.intent), testID: "mail-attachment-retry" } })}
        closeLabel={t("common.close")}
        testID="mail-attachment-failed"
      />
    );
  return { state, start, toast };
}
